import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	fetchEncaissementsV2,
	daterEncaissementsV2,
	daterEncaissementsV2Reset,
} from "../../store/actions/encaissementsV2Actions"
import {
	fetchMoyenPaiements,
	fetchAutreEntites,
} from "../../store/actions/autreActions"
import CustomModal from "../basics/interface/CustomModal"
import AppBtn from "../basics/button/AppBtn"
import Loader from "../basics/Loader"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { round100 } from "../../utilities/Addons"

// Traversée des TransactionV2 non datées (= pas encore encaissées) : filtres
// combinables + sélection multiple + datation en masse (la date vaut
// encaissement ; le caissier n'est jamais réécrit — c'est celui qui a généré
// la transaction, décision Jordan 15.10).
// Périmètre : flux d'argent directs uniquement (cash, carte…) — les
// « Facture » (flux indirect) ont leur circuit sur /facturation et sont
// exclues côté serveur, en lecture comme en datation.
const TABS = [
	{ key: "", label: "Toutes" },
	{ key: "depannage", label: "Dépannage" },
	{ key: "atelier", label: "Atelier" },
	{ key: "location", label: "Location" },
]

const TYPE_LABELS = {
	atelier: "Atelier",
	location: "Location",
	depannage: "Dépannage",
	// Tx d'encaissement terrain (GE-10) : la fiche n'existe pas encore (la
	// conversion re-liera le depannage) — pas de bouton « Ouvrir la fiche ».
	mission: "Dépannage (mission)",
}

const FICHE_ROUTES = {
	atelier: (id) => `/atelier/fiche/${id}`,
	location: (id) => `/location/fiche/${id}`,
	depannage: (id) => `/depannage/fiche/${id}`,
}

const todayISO = () => {
	const d = new Date()
	const pad = (n) => `${n}`.padStart(2, "0")
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Cellules resserrées (padding horizontal 10 au lieu de 16) : 12 colonnes,
// Total et Actions visibles à 1440 px (15.10). tableStyles reste intact.
const serre = (style) => ({ ...style, paddingLeft: 10, paddingRight: 10 })
const T = {
	td: serre(tableStyles.td),
	th: serre(tableStyles.th),
	tdRight: serre(tableStyles.tdRight),
	thRight: serre(tableStyles.thRight),
}

// JJ-MM-AAAA pour l'affichage (l'input date travaille en ISO).
const frDate = (iso) => (iso ? iso.split("-").reverse().join("-") : "")

// Texte long (détails, véhicule) : largeur bornée + ellipse, texte complet
// en infobulle — Total et Actions restent visibles à 1440 px (15.10).
const Tronque = ({ texte, max }) =>
	texte ? (
		<div
			title={texte}
			style={{
				maxWidth: max,
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			}}
		>
			{texte}
		</div>
	) : (
		"—"
	)

// Colonne « N° fiche » (Jordan 15.10) : le clic ouvre la fiche ;
// encaissement terrain pas encore converti → « M<mission> · à convertir ».
const NumeroFiche = ({ row, onOpen }) => {
	if (row.type === "mission")
		return (
			<span style={{ color: COLORS.textMuted, lineHeight: 1.25 }}>
				{`M${row.mission_id}`}
				<br />
				<span style={{ fontSize: 11 }}>à convertir</span>
			</span>
		)
	if (!FICHE_ROUTES[row.type] || !row.fiche_id) return "—"
	return (
		<button
			type="button"
			style={styles.lienFiche}
			onClick={(e) => {
				e.stopPropagation()
				onOpen(row)
			}}
			title="Ouvrir la fiche"
		>
			{`N° ${row.fiche_id}`}
		</button>
	)
}

const EncaissementRow = ({ row, index, checked, onToggle, onOpen }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const veh = row.vehicule || {}
	const vehLabel = [veh.plaques, veh.marque, veh.modele]
		.filter(Boolean)
		.join(" · ")
	return (
		<tr
			onClick={onToggle}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={styles.tdCheck}>
				<input
					type="checkbox"
					checked={checked}
					onChange={onToggle}
					onClick={(e) => e.stopPropagation()}
					style={styles.checkbox}
				/>
			</td>
			<td style={T.td}>{row.created || "—"}</td>
			<td style={T.td}>{row.date_travaux || "—"}</td>
			<td style={T.td}>
				<strong style={{ fontWeight: 600 }}>
					<Tronque texte={row.entite} max={170} />
				</strong>
			</td>
			<td style={T.td}>
				{row.type === "mission" ? (
					<span style={{ lineHeight: 1.25 }}>
						Dépannage
						<br />
						<span style={{ fontSize: 11, color: COLORS.textMuted }}>
							mission
						</span>
					</span>
				) : (
					TYPE_LABELS[row.type] || "—"
				)}
			</td>
			<td style={T.td}>
				<NumeroFiche row={row} onOpen={onOpen} />
			</td>
			<td style={{ ...T.td, color: COLORS.textMuted }}>
				<Tronque texte={vehLabel} max={170} />
			</td>
			<td style={T.td}>{row.paye_par_label || "—"}</td>
			<td style={T.td}>
				<Tronque texte={row.moyen_paie_nom} max={120} />
			</td>
			<td style={{ ...T.td, color: COLORS.textMuted }}>
				<Tronque texte={row.details || row.raison} max={130} />
			</td>
			<td style={styles.tdTotal}>{round100(row.total).toFixed(2)}</td>
			<td style={T.tdRight}>
				{FICHE_ROUTES[row.type] && (
					<button
						style={gs.iconBtn(COLORS.text)}
						onClick={(e) => {
							e.stopPropagation()
							onOpen(row)
						}}
						title="Ouvrir la fiche"
					>
						<FontAwesomeIcon icon={faEye} />
					</button>
				)}
			</td>
		</tr>
	)
}

const EncaissementsV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()

	const [tab, setTab] = useState("")
	const [payePar, setPayePar] = useState("")
	const [assurance, setAssurance] = useState("")
	const [moyen, setMoyen] = useState("")
	const [search, setSearch] = useState("")
	const [selected, setSelected] = useState(() => new Set())
	const [date, setDate] = useState(todayISO())
	const [modalOpen, setModalOpen] = useState(false)

	const { transactions, fetchEncaissementsV2: fetchState, daterEncaissementsV2: daterState } =
		useSelector((state) => state.encaissementsV2Info)
	const moyenPaiements = useSelector((state) => state.autreInfo.moyenPaiements)
	const assurances = useSelector((state) => state.autreInfo.assurances)

	useEffect(() => {
		dispatch(fetchMoyenPaiements())
		dispatch(fetchAutreEntites())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const refetch = () =>
		dispatch(
			fetchEncaissementsV2({
				type: tab,
				paye_par: payePar,
				assurance: payePar === "0" ? assurance : "",
				moyen_paie: moyen,
				search: encodeURIComponent(search.trim()),
			})
		)

	// (Re)charge à chaque changement de filtre ; petit debounce pour la saisie.
	useEffect(() => {
		const timeout = setTimeout(refetch, search ? 300 : 0)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, payePar, assurance, moyen, search])

	// La sélection ne garde que des lignes encore affichées (un changement de
	// filtre ne doit jamais laisser des ids invisibles dans la sélection).
	useEffect(() => {
		setSelected((prev) => {
			const visible = new Set(transactions.map((t) => t.id))
			const next = new Set([...prev].filter((id) => visible.has(id)))
			return next.size === prev.size ? prev : next
		})
	}, [transactions])

	const toggle = (id) =>
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})

	const allChecked =
		transactions.length > 0 && transactions.every((t) => selected.has(t.id))
	const toggleAll = () =>
		setSelected(
			allChecked ? new Set() : new Set(transactions.map((t) => t.id))
		)

	const totalSelection = useMemo(
		() =>
			round100(
				transactions
					.filter((t) => selected.has(t.id))
					.reduce((sum, t) => sum + (t.total || 0), 0)
			),
		[transactions, selected]
	)

	const openFiche = (row) =>
		history.push(FICHE_ROUTES[row.type](row.fiche_id), {
			from: "encaissementsv2",
		})

	// --- datation en masse (modal de confirmation, cf. conventions V2) ----
	const busy = daterState.saving || daterState.saved

	const closeModal = () => {
		if (busy && !daterState.saved) return
		setModalOpen(false)
		dispatch(daterEncaissementsV2Reset())
	}

	const confirmDater = () => {
		if (busy) return
		dispatch(daterEncaissementsV2([...selected], date))
	}

	// ✓ affiché 800 ms, puis fermeture + purge sélection + refetch.
	useEffect(() => {
		if (!daterState.saved) return
		const timeout = setTimeout(() => {
			setModalOpen(false)
			setSelected(new Set())
			dispatch(daterEncaissementsV2Reset())
			refetch()
		}, 800)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [daterState.saved])

	const loading = fetchState.loading
	const nbSelected = selected.size
	const result = daterState.result

	return (
		<>
			{modalOpen && (
				<CustomModal
					title="Encaisser la sélection ?"
					onClose={closeModal}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: closeModal,
							disabled: busy,
						},
						{
							title: daterState.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : daterState.saved ? (
								<FontAwesomeIcon icon={faCheck} />
							) : (
								"Oui"
							),
							styleName: "primary",
							onClick: confirmDater,
							disabled: busy,
						},
					]}
				>
					{daterState.saved && result ? (
						<span>
							{result.updated} transaction
							{result.updated > 1 ? "s" : ""} encaissée
							{result.updated > 1 ? "s" : ""}.
							{result.skipped.length > 0 &&
								` ${result.skipped.length} déjà datée${
									result.skipped.length > 1 ? "s" : ""
								} (ignorée${
									result.skipped.length > 1 ? "s" : ""
								}).`}
						</span>
					) : daterState.error ? (
						<span style={{ color: COLORS.danger }}>
							La datation a échoué. Réessayez ou rechargez la
							page.
						</span>
					) : (
						<span>
							Dater {nbSelected} transaction
							{nbSelected > 1 ? "s" : ""} au {frDate(date)} pour
							un total de {totalSelection.toFixed(2)} CHF ?
						</span>
					)}
				</CustomModal>
			)}

			<div style={gs.page}>
				<div style={{ ...gs.title, marginBottom: 24 }}>
					Encaissements
				</div>

				{/* Type de fiche : segmented control (style /facturation). */}
				<div style={gs.segmented}>
					{TABS.map((t) => (
						<button
							key={t.key}
							type="button"
							style={gs.segment(tab === t.key)}
							onClick={() => setTab(t.key)}
						>
							{t.label}
						</button>
					))}
				</div>

				{/* Filtres logiques, tous combinables (résolus côté serveur). */}
				<div style={styles.filters}>
					<select
						style={styles.filterSelect}
						value={payePar}
						onChange={(e) => {
							setPayePar(e.target.value)
							// Le filtre assurance n'a de sens que payé par Assurance.
							if (e.target.value !== "0") setAssurance("")
						}}
					>
						<option value="">Payé par : tous</option>
						<option value="0">Assurance</option>
						<option value="1">Client</option>
					</select>
					{payePar === "0" && (
						<select
							style={styles.filterSelect}
							value={assurance}
							onChange={(e) => setAssurance(e.target.value)}
						>
							<option value="">Assurance : toutes</option>
							{assurances.map((ass) => (
								<option key={`ass_${ass.id}`} value={ass.id}>
									{ass.label}
								</option>
							))}
						</select>
					)}
					<select
						style={styles.filterSelect}
						value={moyen}
						onChange={(e) => setMoyen(e.target.value)}
					>
						<option value="">Moyen de paiement : tous</option>
						{/* Facture exclue : flux indirect, géré sur /facturation. */}
						{moyenPaiements
							.filter((m) => m.nom !== "Facture")
							.map((m) => (
								<option key={`mdp_${m.id}`} value={m.id}>
									{m.nom}
								</option>
							))}
						<option value="none">Sans moyen de paiement</option>
					</select>
					<input
						style={styles.filterSearch}
						type="text"
						placeholder="Rechercher (client, plaques)…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>

				{/* Barre d'action : visible dès qu'une ligne est cochée. */}
				{nbSelected > 0 && (
					<div style={styles.actionBar}>
						<span style={styles.actionText}>
							{nbSelected} transaction
							{nbSelected > 1 ? "s" : ""} sélectionnée
							{nbSelected > 1 ? "s" : ""} —{" "}
							<strong style={{ fontWeight: 600 }}>
								{totalSelection.toFixed(2)} CHF
							</strong>
						</span>
						<input
							style={styles.dateInput}
							type="date"
							value={date}
							onChange={(e) => setDate(e.target.value)}
						/>
						<AppBtn
							title="Encaisser"
							styleName="primary"
							onClick={() => setModalOpen(true)}
							disabled={!date}
						/>
					</div>
				)}

				<div style={tableStyles.container}>
					{loading ? (
						<Loader variant="ring" message="Chargement…" />
					) : transactions.length === 0 ? (
						<div style={styles.empty}>
							Aucune transaction à encaisser.
						</div>
					) : (
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={styles.thCheck}>
										<input
											type="checkbox"
											checked={allChecked}
											onChange={toggleAll}
											style={styles.checkbox}
											title="Tout sélectionner"
										/>
									</th>
									<th style={T.th}>Créée le</th>
									<th
										style={T.th}
										title="Date des travaux"
									>
										Travaux
									</th>
									<th style={T.th}>Client</th>
									<th style={T.th}>Type</th>
									<th style={T.th}>N° fiche</th>
									<th style={T.th}>Véhicule</th>
									<th style={T.th}>Payé par</th>
									<th style={T.th}>Moyen</th>
									<th style={T.th}>Détails</th>
									<th style={styles.thTotal}>Total (CHF)</th>
									<th style={T.thRight}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{transactions.map((row, i) => (
									<EncaissementRow
										key={`enc_${row.id}`}
										row={row}
										index={i}
										checked={selected.has(row.id)}
										onToggle={() => toggle(row.id)}
										onOpen={openFiche}
									/>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</>
	)
}

const styles = {
	lienFiche: {
		padding: 0,
		border: "none",
		background: "none",
		color: COLORS.accent,
		fontWeight: 600,
		cursor: "pointer",
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
	},
	filters: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		margin: "16px 0",
	},
	filterSelect: {
		...gs.select,
		flex: "0 0 auto",
		minWidth: 210,
	},
	filterSearch: {
		...gs.input,
		flex: "0 1 320px",
	},
	actionBar: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "10px 14px",
		marginBottom: 16,
		border: `1px solid ${COLORS.borderStrong}`,
		borderRadius: 10,
		backgroundColor: COLORS.surface,
	},
	actionText: {
		flex: 1,
		fontSize: 13,
		color: COLORS.text,
	},
	dateInput: {
		...gs.input,
		flex: "0 0 auto",
		width: 150,
	},
	checkbox: {
		width: 15,
		height: 15,
		cursor: "pointer",
	},
	thCheck: {
		...T.th,
		width: 34,
		textAlign: "center",
	},
	tdCheck: {
		...T.td,
		width: 34,
		textAlign: "center",
	},
	thTotal: {
		...T.th,
		textAlign: "right",
	},
	tdTotal: {
		...T.td,
		textAlign: "right",
		fontVariantNumeric: "tabular-nums",
		fontWeight: 600,
	},
	empty: {
		...tableStyles.empty,
		padding: "48px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

export default EncaissementsV2
